/**
 * BarGraphRenderer - Miniblock Activity Timeline
 *
 * Creates a horizontally scrolling bar graph showing miniblock activity over time.
 * Each bar represents a miniblock; height indicates transaction count or gas usage.
 *
 * ## Architecture Overview
 *
 * Bars spawn at the center (sequencer position) and the entire graph scrolls left
 * to make room for new bars. Block boundaries are marked with X-axis labels.
 *
 * ## Key Sections
 *
 * 1. **Bar Management** (lines ~100-200): Creating, updating, removing bars
 * 2. **Scrolling** (lines ~250-350): Continuous leftward scroll animation
 * 3. **Block Tracking** (lines ~350-500): Grouping bars by block, block transitions
 * 4. **Rendering** (lines ~500-700): Drawing bars, grid, labels
 * 5. **Interaction** (lines ~700-900): Click handling, hover states
 * 6. **Modal Support** (lines ~900+): Data for miniblock detail modal
 *
 * ## Key Concepts
 *
 * - **Scroll Offset**: Cumulative horizontal scroll position
 * - **Birth Scroll Offset**: Scroll position when bar was created (for positioning)
 * - **Block Bars**: Groups of bars belonging to the same block
 * - **Expected Count**: Estimated miniblock count (finalized when next block arrives)
 *
 * ## Coordinate System
 *
 * - Bars spawn at SPAWN_X (sequencer center)
 * - Bars grow UPWARD from the baseline
 * - Scroll moves bars LEFT (negative X direction)
 * - X position = SPAWN_X - (scrollOffset - birthScrollOffset)
 *
 * ## Performance Notes
 *
 * - Uses transform-based scrolling for GPU acceleration
 * - Culls off-screen bars during render
 * - Batches graphics operations within update cycle
 */

import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { SCENE_WIDTH, SEQUENCER_CENTER_Y, BLOCK_COLOR, TX_TRAVEL_TIME } from '../config/visualization.config';
import { getBlockColor } from '@/utils/blockColors';
import { formatNumber } from '@/utils/formatters';
import { ThemeManager } from './ThemeManager';
import { SequencerRenderer } from './SequencerRenderer';
import type { PixiThemeColors } from '../config/themes';

interface Bar {
  graphics: Graphics; // Graphics object for rendering
  currentHeight: number;
  targetHeight: number;
  startTime: number;
  x: number; // Base spawn position (always SPAWN_X)
  birthScrollOffset: number; // ScrollOffset when this bar was created
  color: number; // Color of this bar (default or block-specific)
  miniblockData: MiniblockData; // Full miniblock data for modal display
  // glowAlpha removed - no glow effect
}

interface XLabel {
  text: Text;
  x: number;
  birthScrollOffset: number;
  startTime: number; // When the label was created (for fade-in animation)
  blockNumber: number; // Block number for verification status updates
}

export interface MiniblockData {
  blockNumber: number;
  index: number;
  txCount: number;
  gasUsed: string;
  timestamp: number;
}

interface BlockBars {
  blockNumber: number;
  bars: Bar[];
  color: number;
  lastIndex: number; // Last miniblock index seen for this block
  expectedCount: number; // Expected number of miniblocks (estimated, then finalized)
  isComplete: boolean; // Whether we've seen the next block (so count is final)
}

export class BarGraphRenderer {
  private container: Container;
  private barsContainer: Container; // Separate container for bars - uses transform for scroll performance
  private bars: Bar[] = [];
  private xLabels: XLabel[] = [];
  private baselineGraphic: Graphics;
  private gridGraphic: Graphics | null = null;
  public sequencer: SequencerRenderer; // Spinning nested squares showing where bars spawn
  private spawnIndicatorBeam: Graphics; // Beam from sequencer to bar spawn point
  private yAxisLabels: Text[] = [];
  private yAxisLabelBackgrounds: Graphics[] = []; // Individual black backgrounds for Y-axis labels
  private yAxisLabelBackgroundWidths: number[] = []; // Track background widths to avoid unnecessary redraws
  private graphicsPool: Graphics[] = []; // Pool of reusable Graphics objects (eliminates GC)
  private readonly GRAPHICS_POOL_SIZE = 1000; // Pre-allocate 1000 Graphics objects
  private spawnTimer: number = 0;
  private readonly SPAWN_INTERVAL = 2; // ms - spawn bars every 2ms (allows ~8 bars per 16ms frame)
  private readonly BAR_WIDTH = 2; // px
  private readonly MAX_HEIGHT = 300; // px - bars extend upward, baseline at 300 so max bars reach Y=0
  private readonly MINIBLOCK_GAS_MAX = 3_000_000; // 3M gas - used as minimum scale
  private readonly MINIBLOCK_TX_MAX = 5; // Low minimum for better auto-scaling
  private readonly GROWTH_DURATION = 50; // ms - fast snappy animation
  private readonly SCROLL_AMOUNT = 2; // px
  // NOTE: This is the per-frame factor at 60fps. Delta-time compensation scales it for other frame rates.
  private readonly SCROLL_INTERPOLATION = 0.02; // Smooth scroll - lower = smoother but slower to catch up
  private readonly SPAWN_X = SCENE_WIDTH / 2 - 100; // Shifted 100px left from center
  private readonly SPAWN_OFFSET = 100; // Visual offset to compensate for scroll lag
  private readonly BASELINE_Y: number;
  private readonly SPAWN_INDICATOR_SIZE = 40; // Size of spawn indicator square (px)
  private readonly SPAWN_INDICATOR_OFFSET_Y = 40; // Distance below baseline (px)
  // NOTE: This is the per-frame factor at 60fps. Delta-time compensation scales it for other frame rates.
  private readonly SPAWN_INDICATOR_INTERPOLATION = 0.02; // Smooth easing - lower = smoother
  private readonly SPAWN_INDICATOR_STOP_THRESHOLD = 2; // Stop moving within 2px of target
  private spawnIndicatorX: number = 0; // Current X position of spawn indicator

  // Beam configuration (synced with sequencer speed)
  private readonly BEAM_WIDTH_BOTTOM = 10;  // Width at sequencer end (matches outer square feel)
  private readonly BEAM_WIDTH_TOP = 2;  // Width at graph baseline (px)
  private readonly BEAM_ALPHA_MIN = 0.3;  // Alpha when idle
  private readonly BEAM_ALPHA_MAX = 0.9;  // Alpha at max speed
  // OPTIMIZATION: Pre-allocated circular buffer instead of dynamic array (avoids per-frame allocations)
  private readonly MOVEMENT_QUEUE_SIZE = 64; // Max queue entries (450ms / 16ms ~= 28, with buffer)
  private movementQueueX: Float32Array = new Float32Array(this.MOVEMENT_QUEUE_SIZE);
  private movementQueueTime: Float32Array = new Float32Array(this.MOVEMENT_QUEUE_SIZE);
  private movementQueueHead: number = 0; // Read index
  private movementQueueTail: number = 0; // Write index
  private movementQueueLength: number = 0; // Current queue length
  private scrollOffset: number = 0; // Actual scroll position (smoothly interpolated)
  private targetScrollOffset: number = 0; // Target scroll position (jumps with bar spawns)
  private barCounter: number = 0; // Exact position counter for perfect 2px spacing (not interpolated)

  // Dynamic scaling (TradingView-style)
  private currentGasMax: number = this.MINIBLOCK_GAS_MAX; // Current max for gas scale
  private currentTxMax: number = this.MINIBLOCK_TX_MAX; // Current max for tx count scale
  private targetGasMax: number = this.MINIBLOCK_GAS_MAX; // Target max for smooth transitions
  private targetTxMax: number = this.MINIBLOCK_TX_MAX; // Target max for smooth transitions
  private readonly SCALE_PADDING = 1.15; // 15% padding above max value (tighter fit)
  private readonly SCALE_LERP_SPEED = 0.15; // Faster interpolation for more responsive scaling

  // Store actual values for each bar (not normalized heights)
  private barGasValues: Map<Bar, number> = new Map(); // gasUsed for each bar
  private barTxValues: Map<Bar, number> = new Map(); // txCount for each bar

  // Callback to get next miniblock from pending array
  private getNextMiniblock: (() => MiniblockData | null) | null = null;

  // Callback to get block verification status
  private getBlockVerification: ((blockNumber: number) => { blockNumber: number; expected: number; received: number; status: 'pending' | 'verified' | 'warning' } | null) | null = null;

  // Block tracking
  private blockBars = new Map<number, BlockBars>();
  private currentBlockNumber: number = -1; // Track current block for completion detection

  // Chart mode (gas usage vs transaction count)
  private chartMode: 'gas' | 'txCount' = 'txCount';
  private chartModeChanged: boolean = false; // Flag to force recalculation on mode change

  // Click handling
  private onBarClick: ((miniblock: MiniblockData) => void) | null = null;
  private isPaused: boolean = false;

  // Dev mode - shows verification icons when true
  private devMode: boolean = false;

  // Label update throttling
  private lastLabelUpdate: number = 0;
  // OPTIMIZATION: Increased from 200ms to 500ms for older devices
  private readonly LABEL_UPDATE_INTERVAL = 500;

  // Theme colors (reactive)
  private colors: PixiThemeColors;
  private unsubscribeTheme: (() => void) | null = null;

  private sequencerContainer: Container; // Separate container for sequencer (interactive)

  constructor(parentContainer: Container, sequencerContainer: Container) {
    this.sequencerContainer = sequencerContainer;
    this.container = new Container();
    parentContainer.addChild(this.container);

    // Initialize theme colors
    this.colors = ThemeManager.colors;

    // Subscribe to theme changes
    this.unsubscribeTheme = ThemeManager.subscribe((colors) => {
      this.colors = colors;
      this.applyTheme();
    });

    // Position bar graph baseline so max height bars (150px) reach top of screen (Y=0)
    // Parent container is at Y=0 (fixed in PixiAppSetup)
    // Baseline at 150px, bars extend upward to 0
    this.BASELINE_Y = this.MAX_HEIGHT;
    this.container.y = this.BASELINE_Y;

    // Create separate container for bars (performance: single transform instead of updating each bar)
    this.barsContainer = new Container();
    this.barsContainer.eventMode = 'none';
    this.container.addChild(this.barsContainer);

    // Draw baseline (X-axis) - uses same color as grid lines
    this.baselineGraphic = new Graphics();
    this.baselineGraphic.moveTo(0, 0);
    this.baselineGraphic.lineTo(SCENE_WIDTH, 0);
    this.baselineGraphic.stroke({ width: 1, color: this.colors.border, alpha: 0.5 });
    this.container.addChild(this.baselineGraphic);

    // Y-axis line removed per user request
    // this.yAxisGraphic remains null

    // Create grid graphic (will be drawn in createYAxisLabels)
    this.gridGraphic = new Graphics();
    this.container.addChildAt(this.gridGraphic, 0); // Behind everything

    // Create Y-axis labels based on current mode
    this.createYAxisLabels();

    // Create sequencer (spinning nested squares) - added to dedicated interactive container
    this.sequencer = new SequencerRenderer(this.sequencerContainer);
    this.sequencer.setPosition(this.SPAWN_X + this.SPAWN_OFFSET, this.BASELINE_Y + this.SPAWN_INDICATOR_OFFSET_Y);

    // Create beam from sequencer to bar spawn point
    this.spawnIndicatorBeam = new Graphics();
    this.container.addChild(this.spawnIndicatorBeam);

    // Initialize spawn indicator position at spawn point
    this.spawnIndicatorX = this.SPAWN_X + this.SPAWN_OFFSET;

    // Pre-allocate Graphics objects pool (eliminates GC from constant allocation/deallocation)
    for (let i = 0; i < this.GRAPHICS_POOL_SIZE; i++) {
      this.graphicsPool.push(new Graphics());
    }

    // console.log('📊 BarGraphRenderer initialized at Y:', this.BASELINE_Y);
    // console.log(`📊 Graphics pool pre-allocated: ${this.GRAPHICS_POOL_SIZE} objects`);
  }

  /**
   * Get a Graphics object from the pool (or create new one if pool is empty)
   */
  private getGraphicsFromPool(): Graphics {
    const graphics = this.graphicsPool.pop();
    if (graphics) {
      return graphics;
    }
    // Pool is empty - create new Graphics object (shouldn't happen with proper pool size)
    return new Graphics();
  }

  /**
   * Return a Graphics object to the pool for reuse
   */
  private returnGraphicsToPool(graphics: Graphics): void {
    // Enforce max pool size to prevent unbounded growth
    if (this.graphicsPool.length >= this.GRAPHICS_POOL_SIZE * 2) {
      // Pool is over capacity - destroy the graphics instead of returning
      graphics.destroy();
      return;
    }
    // Clear the graphics object for reuse
    graphics.clear();
    graphics.x = 0;
    graphics.y = 0;
    graphics.visible = true;
    // Return to pool
    this.graphicsPool.push(graphics);
  }

  /**
   * Create or update Y-axis labels based on chart mode and current dynamic scale
   * Labels positioned at right edge of canvas with individual black backgrounds
   */
  private createYAxisLabels(): void {
    const isFirstTime = this.yAxisLabels.length === 0;

    // Get max value and format function based on chart mode
    const maxValue = this.chartMode === 'gas' ? this.currentGasMax : this.currentTxMax;
    const step = maxValue / 4; // 5 labels: 0, 25%, 50%, 75%, 100%

    for (let i = 0; i <= 4; i++) {
      const value = step * i;
      const heightRatio = value / maxValue;
      const barHeight = heightRatio * this.MAX_HEIGHT;
      let yPosition = -barHeight;

      // Format label based on mode
      const labelStr = this.chartMode === 'gas' ? formatNumber(value) : `${Math.round(value)}`;

      if (isFirstTime) {
        this.createYAxisLabel(i, labelStr, yPosition);
      } else {
        this.updateYAxisLabel(i, labelStr, yPosition);
      }
    }

    // Draw grid lines at label positions
    this.drawGridLines();
  }

  /**
   * Create a new Y-axis label with background
   */
  private createYAxisLabel(index: number, labelStr: string, yPosition: number): void {
    // Create black background box
    const bgGraphic = new Graphics();
    this.container.addChild(bgGraphic);
    this.yAxisLabelBackgrounds.push(bgGraphic);

    // Create text label (uses theme color)
    const labelText = new Text({
      text: labelStr,
      style: {
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 16,
        fontWeight: '700',
        fill: this.colors.textSecondary,
      }
    });

    labelText.x = SCENE_WIDTH;
    labelText.anchor.set(1, 0.5); // Right-center: right edge at canvas edge

    this.container.addChild(labelText);
    this.yAxisLabels.push(labelText);

    const padding = 4;
    const boxHeight = labelText.height + padding * 2;
    const halfBoxHeight = boxHeight / 2;

    // Clamp top and bottom labels to stay within chart bounds
    yPosition = this.clampLabelPosition(index, yPosition, halfBoxHeight);
    labelText.y = yPosition;

    // Draw background box at relative coordinates (0, 0) centered vertically
    const boxWidth = labelText.width + padding * 2;
    bgGraphic.rect(0, -halfBoxHeight, boxWidth, boxHeight);
    bgGraphic.fill(this.colors.bgSurface); // Theme background

    // Position the background graphics object
    bgGraphic.x = labelText.x - boxWidth;
    bgGraphic.y = labelText.y;

    // Store width to track changes
    this.yAxisLabelBackgroundWidths.push(boxWidth);
  }

  /**
   * Update an existing Y-axis label and its background
   */
  private updateYAxisLabel(index: number, labelStr: string, yPosition: number): void {
    const labelText = this.yAxisLabels[index];
    const bgGraphic = this.yAxisLabelBackgrounds[index];

    labelText.text = labelStr;

    const padding = 4;
    const boxHeight = labelText.height + padding * 2;
    const halfBoxHeight = boxHeight / 2;

    // Clamp top and bottom labels to stay within chart bounds
    yPosition = this.clampLabelPosition(index, yPosition, halfBoxHeight);
    labelText.y = yPosition;

    // Only redraw background if width changed significantly (>5px) - performance optimization
    const boxWidth = labelText.width + padding * 2;
    const prevWidth = this.yAxisLabelBackgroundWidths[index] || 0;
    if (Math.abs(boxWidth - prevWidth) > 5) {
      bgGraphic.clear();
      bgGraphic.rect(0, -halfBoxHeight, boxWidth, boxHeight);
      bgGraphic.fill(this.colors.bgSurface); // Theme background
      this.yAxisLabelBackgroundWidths[index] = boxWidth; // Update stored width
    }

    // Always update position to match label
    bgGraphic.x = labelText.x - boxWidth;
    bgGraphic.y = labelText.y;
  }

  /**
   * Clamp label Y position to stay within chart bounds
   */
  private clampLabelPosition(index: number, yPosition: number, halfBoxHeight: number): number {
    // Clamp top and bottom labels to stay within chart bounds
    if (index === 0 || index === 4) {
      // Top label: don't extend above -MAX_HEIGHT
      if (yPosition < -this.MAX_HEIGHT + halfBoxHeight) {
        yPosition = -this.MAX_HEIGHT + halfBoxHeight;
      }
      // Bottom label: don't extend below baseline (Y=0)
      if (yPosition > -halfBoxHeight) {
        yPosition = -halfBoxHeight;
      }
    }
    return yPosition;
  }

  /**
   * Draw horizontal grid lines at Y-axis label positions
   * Grid lines are drawn at 25%, 50%, 75% positions (label indices 1, 2, 3)
   * Staggered start: bottom extends 50px left, middle 25px left, top at spawn
   */
  private drawGridLines(): void {
    if (!this.gridGraphic || this.yAxisLabels.length < 4) return;

    this.gridGraphic.clear();

    // Staggered offsets: bottom (i=1) = -50px, middle (i=2) = -25px, top (i=3) = 0px
    const offsets = [0, -50, -25, 0]; // index 0 unused, 1=bottom, 2=middle, 3=top

    // Draw grid lines at 25%, 50%, 75% label positions (indices 1, 2, 3)
    for (let i = 1; i <= 3; i++) {
      const label = this.yAxisLabels[i];
      const gridY = label.y;
      const startX = this.SPAWN_X + offsets[i];

      this.gridGraphic.moveTo(startX, gridY);
      this.gridGraphic.lineTo(SCENE_WIDTH, gridY);
    }
    this.gridGraphic.stroke({ width: 1, color: this.colors.border, alpha: 0.5 });
  }

  /**
   * Round max value to create nice grid labels
   * Rounds up so that max/4 gives whole numbers (or 1 decimal place for small values)
   * This ensures bars never exceed the chart bounds
   * Examples: 8 -> 8 (steps: 2,4,6,8), 10 -> 12 (steps: 3,6,9,12), 492 -> 492 (steps: 123,246,369,492)
   */
  private roundMaxForGrid(max: number): number {
    const step = max / 4;

    let roundedStep: number;
    if (step >= 1) {
      // Round step up to whole number
      roundedStep = Math.ceil(step);
    } else {
      // Round step up to 1 decimal place for small values
      roundedStep = Math.ceil(step * 10) / 10;
    }

    // Ensure minimum step
    if (roundedStep === 0) {
      roundedStep = 0.1;
    }

    return roundedStep * 4;
  }

  /**
   * Set callback to get next miniblock from pending array
   */
  public setMiniblockGetter(getter: () => MiniblockData | null): void {
    this.getNextMiniblock = getter;
  }

  /**
   * Set chart mode and update Y-axis labels
   */
  public setChartMode(mode: 'gas' | 'txCount'): void {
    if (this.chartMode === mode) return; // No change
    this.chartMode = mode;
    this.chartModeChanged = true; // Flag to force bar height recalculation
    this.createYAxisLabels();
  }

  /**
   * Instantly create multiple bars from buffered miniblocks (used on resume)
   * All bars are created at full height immediately without animation
   */
  public createInstantBars(miniblocks: MiniblockData[]): void {
    const now = performance.now();

    // console.log(`📊 Creating ${miniblocks.length} instant bars`);

    for (const miniblock of miniblocks) {
      // Parse gas used
      let gasUsed = 0;
      try {
        const gasString = String(miniblock.gasUsed);
        gasUsed = gasString.startsWith('0x')
          ? parseInt(gasString, 16)
          : parseInt(gasString, 10);
      } catch (e) {
        gasUsed = 0;
      }

      const txCount = miniblock.txCount;

      // Calculate bar height based on current mode and dynamic scale
      let targetHeight: number;
      if (this.chartMode === 'gas') {
        const heightRatio = Math.min(gasUsed / this.currentGasMax, 1.0);
        targetHeight = heightRatio * this.MAX_HEIGHT;
      } else {
        const heightRatio = Math.min(txCount / this.currentTxMax, 1.0);
        targetHeight = heightRatio * this.MAX_HEIGHT;
      }

      // Get Graphics object from pool (or create if pool exhausted)
      const graphics = this.getGraphicsFromPool();
      this.barsContainer.addChild(graphics); // Add to barsContainer for transform-based scrolling

      // Interactivity controlled by container.eventMode (toggled in setPausedState)
      // No individual eventMode or cursor set - bars inherit from parent container

      // Position bar using exact counter for perfect 2px spacing, with offset for visual spawn point
      graphics.x = this.SPAWN_X + this.SPAWN_OFFSET - this.barCounter;
      graphics.y = 0;

      // Generate color
      const blockColor = getBlockColor(miniblock.blockNumber);

      // Draw bar at full height immediately (extending upward from baseline)
      graphics.rect(0, -targetHeight, this.BAR_WIDTH, targetHeight);
      graphics.fill(blockColor);

      // Create bar with full height (already animated)
      const bar: Bar = {
        graphics,
        currentHeight: targetHeight,
        targetHeight,
        startTime: now - this.GROWTH_DURATION, // Set in past so animation is complete
        x: this.SPAWN_X,
        birthScrollOffset: this.barCounter,
        color: blockColor,
        miniblockData: miniblock
      };

      // Add click handler
      graphics.on('pointerdown', () => {
        if (this.isPaused && this.onBarClick) {
          this.onBarClick(miniblock);
        }
      });

      this.bars.push(bar);

      // Store actual values for dynamic scaling
      this.barGasValues.set(bar, gasUsed);
      this.barTxValues.set(bar, txCount);

      // Track bar for block completion
      this.trackBarForBlock(miniblock, bar);

      // Advance exact position counter and scroll offsets together for instant creation
      this.barCounter += this.SCROLL_AMOUNT;
      this.scrollOffset = this.barCounter;
      this.targetScrollOffset = this.barCounter;
    }

    // Position updates handled by container transform in main update() loop
  }

  // Debug: track spawn rate
  private spawnCount: number = 0;
  private lastSpawnLogTime: number = 0;

  /**
   * Update bar animations and spawn new bars
   */
  public update(deltaMs: number): void {
    const now = performance.now();

    // Update spawn timer - spawn multiple bars per frame if needed
    this.spawnTimer += deltaMs;
    let spawnedThisFrame = 0;
    while (this.spawnTimer >= this.SPAWN_INTERVAL) {
      const spawned = this.spawnBar(now);
      if (spawned) spawnedThisFrame++;
      this.spawnTimer -= this.SPAWN_INTERVAL;
    }

    // Smoothly interpolate scroll position toward target (prevents jerky scrolling when data arrives in bunches)
    // OPTIMIZATION: Delta-time aware interpolation - consistent speed at any FPS
    const scrollDiff = this.targetScrollOffset - this.scrollOffset;
    const scrollLerpFactor = 1 - Math.pow(1 - this.SCROLL_INTERPOLATION, deltaMs / 16.67);
    this.scrollOffset += scrollDiff * scrollLerpFactor;

    // Update container transform for scrolling (single operation replaces ~350k individual updates/sec)
    this.barsContainer.x = this.scrollOffset;

    // Update spawn indicator position with movement queue
    const barSpawnScreenX = (this.SPAWN_X + this.SPAWN_OFFSET - this.barCounter) + this.scrollOffset;
    const diff = barSpawnScreenX - this.spawnIndicatorX;

    // OPTIMIZATION: Use pre-allocated circular buffer instead of dynamic array
    // Remove executed movements from queue (movements that are now in the past)
    while (this.movementQueueLength > 0 &&
           this.movementQueueTime[this.movementQueueHead] <= now) {
      this.movementQueueHead = (this.movementQueueHead + 1) % this.MOVEMENT_QUEUE_SIZE;
      this.movementQueueLength--;
    }

    // Add new planned movements to extend the queue with smooth easing
    const planningHorizon = TX_TRAVEL_TIME * 1.125; // Plan 450ms ahead (50ms buffer ensures queue is always ready)

    // Find the last planned time (or use now if queue is empty)
    const lastTailIndex = (this.movementQueueTail - 1 + this.MOVEMENT_QUEUE_SIZE) % this.MOVEMENT_QUEUE_SIZE;
    const lastPlannedTime = this.movementQueueLength > 0
      ? this.movementQueueTime[lastTailIndex]
      : now;

    // Only add new movements if we need to extend the planning horizon
    const neededPlanningTime = now + planningHorizon;

    if (lastPlannedTime < neededPlanningTime && Math.abs(diff) > this.SPAWN_INDICATOR_STOP_THRESHOLD) {
      // Start planning from the last planned position (or current position if queue is empty)
      let currentPlanX = this.movementQueueLength > 0
        ? this.movementQueueX[lastTailIndex]
        : this.spawnIndicatorX;

      // Add movements from lastPlannedTime to neededPlanningTime with easing
      let planTime = lastPlannedTime + deltaMs;
      while (planTime <= neededPlanningTime && this.movementQueueLength < this.MOVEMENT_QUEUE_SIZE) {
        // Calculate distance from planned position to target
        const distanceToTarget = barSpawnScreenX - currentPlanX;

        // Stop planning if within threshold
        if (Math.abs(distanceToTarget) <= this.SPAWN_INDICATOR_STOP_THRESHOLD) {
          break;
        }

        // Smooth easing: move proportionally to distance (faster when far, slower when close)
        const moveAmount = distanceToTarget * this.SPAWN_INDICATOR_INTERPOLATION;
        currentPlanX += moveAmount;

        // Add to circular buffer (no object allocation!)
        this.movementQueueX[this.movementQueueTail] = currentPlanX;
        this.movementQueueTime[this.movementQueueTail] = planTime;
        this.movementQueueTail = (this.movementQueueTail + 1) % this.MOVEMENT_QUEUE_SIZE;
        this.movementQueueLength++;

        planTime += deltaMs;
      }
    }

    // Execute actual movement with smooth easing (use first queued position if available)
    if (this.movementQueueLength > 0) {
      // Move to the first queued position
      this.spawnIndicatorX = this.movementQueueX[this.movementQueueHead];
    } else if (Math.abs(diff) > this.SPAWN_INDICATOR_STOP_THRESHOLD) {
      // Fallback if queue is empty - smooth interpolation with delta-time awareness
      const indicatorLerpFactor = 1 - Math.pow(1 - this.SPAWN_INDICATOR_INTERPOLATION, deltaMs / 16.67);
      this.spawnIndicatorX += diff * indicatorLerpFactor;
    }

    // Update sequencer position and animation
    this.sequencer.setPosition(this.spawnIndicatorX, this.BASELINE_Y + this.SPAWN_INDICATOR_OFFSET_Y);
    this.sequencer.update(deltaMs);

    // Get current block color (use the most recent block being spawned)
    const currentBlockNumber = this.currentBlockNumber >= 0 ? this.currentBlockNumber : 0;
    const beamColor = getBlockColor(currentBlockNumber);

    // Update sequencer color to match current block
    this.sequencer.updateBlockColor(currentBlockNumber);

    // Calculate beam alpha synced with sequencer speed (1.0 = idle, up to 25 = max)
    const speedMultiplier = this.sequencer.getSpeedMultiplier();
    const speedNormalized = (speedMultiplier - 1.0) / 24.0; // Normalize 1-25 to 0-1
    const beamAlpha = this.BEAM_ALPHA_MIN + speedNormalized * (this.BEAM_ALPHA_MAX - this.BEAM_ALPHA_MIN);

    // Update beam from sequencer to bar spawn point
    this.spawnIndicatorBeam.clear();

    // Draw simple line from top edge of innermost square to bar spawn point
    const beamStartX = this.spawnIndicatorX;
    const beamStartY = this.SPAWN_INDICATOR_OFFSET_Y - 3; // Top of innermost square (6px / 2)
    const beamEndX = barSpawnScreenX + 4; // Spawn point
    const beamEndY = 0; // Baseline

    this.spawnIndicatorBeam.moveTo(beamStartX, beamStartY);
    this.spawnIndicatorBeam.lineTo(beamEndX, beamEndY);
    this.spawnIndicatorBeam.stroke({ width: 2, color: beamColor, alpha: beamAlpha });

    // Calculate visible max inline during bar updates (optimized to avoid double iteration)
    let maxGas = this.MINIBLOCK_GAS_MAX; // Minimum scale
    let maxTx = this.MINIBLOCK_TX_MAX; // Minimum scale

    // Store previous scale values BEFORE interpolation
    const prevGasMax = this.currentGasMax;
    const prevTxMax = this.currentTxMax;

    // Interpolate current scale towards target FIRST (must happen before we check for changes)
    // OPTIMIZATION: Delta-time aware interpolation - consistent speed at any FPS
    const scaleLerpFactor = 1 - Math.pow(1 - this.SCALE_LERP_SPEED, deltaMs / 16.67);
    this.currentGasMax += (this.targetGasMax - this.currentGasMax) * scaleLerpFactor;
    this.currentTxMax += (this.targetTxMax - this.currentTxMax) * scaleLerpFactor;

    // OPTIMIZATION: Increased threshold from 3% to 8% - reduces label updates on older devices
    const gasMaxChanged = Math.abs(this.currentGasMax - prevGasMax) / prevGasMax > 0.08;
    const txMaxChanged = Math.abs(this.currentTxMax - prevTxMax) / prevTxMax > 0.08;
    const scaleChanged = (this.chartMode === 'gas' && gasMaxChanged) || (this.chartMode === 'txCount' && txMaxChanged);

    // Force recalculation if chart mode changed OR scale changed
    const needsRecalculation = this.chartModeChanged || scaleChanged;

    // Update existing bars (growth animation AND position, AND dynamic rescaling, AND visible max calculation)
    for (let i = this.bars.length - 1; i >= 0; i--) {
      const bar = this.bars[i];
      const elapsed = now - bar.startTime;
      // Use ease-out cubic for smoother animation
      const t = Math.min(elapsed / this.GROWTH_DURATION, 1.0);
      const progress = 1 - Math.pow(1 - t, 3); // ease-out cubic

      // Recalculate target height if scale changed, mode changed, OR bar finished animating
      // (finished bars need to resize continuously as scale interpolates)
      if (needsRecalculation || progress >= 1.0) {
        let newTargetHeight: number;
        if (this.chartMode === 'gas') {
          const gasValue = this.barGasValues.get(bar) || 0;
          const heightRatio = Math.min(gasValue / this.currentGasMax, 1.0);
          newTargetHeight = heightRatio * this.MAX_HEIGHT;
        } else {
          const txValue = this.barTxValues.get(bar) || 0;
          const heightRatio = Math.min(txValue / this.currentTxMax, 1.0);
          newTargetHeight = heightRatio * this.MAX_HEIGHT;
        }
        bar.targetHeight = newTargetHeight;
      }

      // Calculate screen position from static bar position + container transform
      const screenX = bar.graphics.x + this.barsContainer.x;

      // Calculate alpha based on position within block (per-block gradient)
      // First miniblock in block = brightest (1.0), last = dimmest (0.4)
      const targetAlpha = this.getBarAlpha(bar);

      // Animate height growth - redraw only if visible change AND not completed
      // OPTIMIZATION: Increased thresholds from 1.0/0.02 to 3.0/0.05 - reduces redraws by ~70%
      const newHeight = bar.targetHeight * progress;
      const heightDiff = Math.abs(newHeight - bar.currentHeight);
      const alphaDiff = Math.abs(bar.graphics.alpha - targetAlpha);
      const shouldRedraw = (heightDiff > 3.0 && progress < 1.0) || alphaDiff > 0.05;

      if (shouldRedraw) {
        bar.currentHeight = newHeight;
        bar.graphics.alpha = targetAlpha;

        // Redraw bar (extending upward from baseline)
        bar.graphics.clear();
        bar.graphics.rect(0, -bar.currentHeight, this.BAR_WIDTH, bar.currentHeight);
        bar.graphics.fill(bar.color);
      } else if (progress >= 1.0 && (heightDiff > 0.5 || alphaDiff > 0.05)) {
        // Snap to final position for completed animations (also increased threshold)
        bar.currentHeight = bar.targetHeight;
        bar.graphics.alpha = targetAlpha;
        bar.graphics.clear();
        bar.graphics.rect(0, -bar.currentHeight, this.BAR_WIDTH, bar.currentHeight);
        bar.graphics.fill(bar.color);
      }

      // Calculate visible max inline (only for bars on screen)
      if (screenX >= 0 && screenX <= SCENE_WIDTH) {
        const gasValue = this.barGasValues.get(bar) || 0;
        const txValue = this.barTxValues.get(bar) || 0;
        maxGas = Math.max(maxGas, gasValue);
        maxTx = Math.max(maxTx, txValue);
      }

      // Remove bars that have scrolled off-screen to the right
      if (screenX > SCENE_WIDTH) {
        this.barsContainer.removeChild(bar.graphics);
        // Return Graphics to pool for reuse (eliminates GC)
        this.returnGraphicsToPool(bar.graphics);
        // Clean up stored values
        this.barGasValues.delete(bar);
        this.barTxValues.delete(bar);
        // Clean up block tracking for completed blocks when all bars are gone
        const blockData = this.blockBars.get(bar.miniblockData.blockNumber);
        if (blockData && blockData.isComplete) {
          const barIndex = blockData.bars.indexOf(bar);
          if (barIndex !== -1) {
            blockData.bars.splice(barIndex, 1);
          }
          // Remove block entry when all bars are gone
          if (blockData.bars.length === 0) {
            this.blockBars.delete(bar.miniblockData.blockNumber);
          }
        }
        // Swap-with-last removal (O(1) instead of O(n) splice)
        const lastIndex = this.bars.length - 1;
        if (i !== lastIndex) {
          this.bars[i] = this.bars[lastIndex];
        }
        this.bars.pop();
      }
    }

    // Apply padding to visible max
    maxGas = Math.ceil(maxGas * this.SCALE_PADDING);
    maxTx = Math.ceil(maxTx * this.SCALE_PADDING);

    // Round to nice values for grid labels (divisible by 4 for even steps)
    this.targetGasMax = this.roundMaxForGrid(maxGas);
    this.targetTxMax = this.roundMaxForGrid(maxTx);

    // Note: Interpolation already happened at the start of update() before the loop

    // Update Y-axis labels if scale changed significantly AND throttle time has passed
    if (scaleChanged && (now - this.lastLabelUpdate) > this.LABEL_UPDATE_INTERVAL) {
      this.createYAxisLabels();
      this.lastLabelUpdate = now;
    }

    // Reset chart mode changed flag after processing
    if (this.chartModeChanged) {
      this.chartModeChanged = false;
    }

    // Check for labels that scrolled off-screen and animate fade-in
    for (let i = this.xLabels.length - 1; i >= 0; i--) {
      const label = this.xLabels[i];
      const screenX = label.text.x + this.barsContainer.x;

      // Animate fade-in for new labels (300ms duration)
      const elapsed = now - label.startTime;
      const FADE_DURATION = 300;
      if (elapsed < FADE_DURATION) {
        label.text.alpha = Math.min(elapsed / FADE_DURATION, 1.0);
      } else if (label.text.alpha < 1.0) {
        label.text.alpha = 1.0; // Snap to final alpha
      }

      // Remove labels that scrolled off-screen
      if (screenX > SCENE_WIDTH) {
        this.barsContainer.removeChild(label.text);
        // Swap-with-last removal (O(1) instead of O(n) splice)
        const lastIndex = this.xLabels.length - 1;
        if (i !== lastIndex) {
          this.xLabels[i] = this.xLabels[lastIndex];
        }
        this.xLabels.pop();
      }
    }
  }

  /**
   * Spawn a new bar at the center spawn point using miniblock data
   * @returns true if a bar was spawned, false if no miniblock available
   */
  private spawnBar(now: number): boolean {
    // Get next miniblock from pending array (lowest block number + index)
    if (!this.getNextMiniblock) {
      return false;
    }

    const miniblock = this.getNextMiniblock();
    if (!miniblock) {
      // No miniblocks available (normal during initial load)
      return false;
    }

    // Parse gas used (could be hex "0x..." or decimal string or number)
    let gasUsed = 0;
    try {
      const gasString = String(miniblock.gasUsed);
      gasUsed = gasString.startsWith('0x')
        ? parseInt(gasString, 16)
        : parseInt(gasString, 10);
    } catch (e) {
      gasUsed = 0;
    }

    const txCount = miniblock.txCount;

    // Calculate bar height based on current mode and dynamic scale
    let targetHeight: number;
    if (this.chartMode === 'gas') {
      const heightRatio = Math.min(gasUsed / this.currentGasMax, 1.0);
      targetHeight = heightRatio * this.MAX_HEIGHT;
    } else {
      const heightRatio = Math.min(txCount / this.currentTxMax, 1.0);
      targetHeight = heightRatio * this.MAX_HEIGHT;
    }

    // Get Graphics object from pool (or create if pool exhausted)
    const graphics = this.getGraphicsFromPool();
    this.barsContainer.addChild(graphics); // Add to barsContainer for transform-based scrolling

    // Interactivity controlled by container.eventMode (toggled in setPausedState)
    // No individual eventMode or cursor set - bars inherit from parent container

    // Position bar using exact counter for perfect 2px spacing, with offset for visual spawn point
    // Container transform adds smoothly interpolated scroll for visual smoothness
    graphics.x = this.SPAWN_X + this.SPAWN_OFFSET - this.barCounter;
    graphics.y = 0; // Baseline

    // Generate color based on block number immediately (memoized)
    const blockColor = getBlockColor(miniblock.blockNumber);

    // Start with 0 height for growth animation
    // Bar will grow from 0 to targetHeight in the update loop
    graphics.rect(0, 0, this.BAR_WIDTH, 0);
    graphics.fill(blockColor);

    const bar: Bar = {
      graphics,
      currentHeight: 0,
      targetHeight,
      startTime: now,
      x: this.SPAWN_X, // Base position (center)
      birthScrollOffset: this.barCounter, // Remember exact position counter when this bar was born
      color: blockColor, // Use block color immediately (deterministic from block number)
      miniblockData: miniblock
    };

    // Add click handler
    graphics.on('pointerdown', () => {
      if (this.isPaused && this.onBarClick) {
        this.onBarClick(miniblock);
      }
    });

    this.bars.push(bar);

    // Store actual values for dynamic scaling
    this.barGasValues.set(bar, gasUsed);
    this.barTxValues.set(bar, txCount);

    // Track bar for block completion
    this.trackBarForBlock(miniblock, bar);

    // Advance exact position counter for perfect spacing
    this.barCounter += this.SCROLL_AMOUNT;
    // Update target scroll to match (actual scroll will smoothly interpolate toward this)
    this.targetScrollOffset = this.barCounter;
    // Position updates handled by container transform in main update() loop

    return true;
  }

  /**
   * Track a bar for block completion and labeling
   */
  private trackBarForBlock(miniblock: MiniblockData, bar: Bar): void {
    const blockNumber = miniblock.blockNumber;

    // Detect block transition - complete previous block when we see a new block number
    if (this.currentBlockNumber !== -1 && blockNumber !== this.currentBlockNumber) {
      const previousBlockData = this.blockBars.get(this.currentBlockNumber);
      if (previousBlockData) {
        // Mark as complete
        previousBlockData.isComplete = true;
        previousBlockData.expectedCount = previousBlockData.bars.length;
        this.addBlockLabel(this.currentBlockNumber, previousBlockData);
      }
    }
    this.currentBlockNumber = blockNumber;

    // Get or create block tracking entry
    if (!this.blockBars.has(blockNumber)) {
      const blockColor = getBlockColor(blockNumber);
      this.blockBars.set(blockNumber, {
        blockNumber,
        bars: [],
        color: blockColor,
        lastIndex: -1,
        expectedCount: 10, // Initial estimate, will be updated when block completes
        isComplete: false
      });
    }

    const blockData = this.blockBars.get(blockNumber)!;
    blockData.bars.push(bar);

    // Track last index for this block
    blockData.lastIndex = Math.max(blockData.lastIndex, miniblock.index);

    // Update expected count estimate as we see more miniblocks
    // Use current count + 2 as a rolling estimate until block completes
    if (!blockData.isComplete) {
      blockData.expectedCount = Math.max(blockData.expectedCount, blockData.bars.length + 2);
    }
  }

  /**
   * Get the target alpha for a bar (constant - no gradient)
   */
  private getBarAlpha(_bar: Bar): number {
    return 1.0;
  }

  /**
   * Add label for a completed block
   */
  private addBlockLabel(blockNumber: number, blockData: BlockBars): void {
    // Add block label centered between first and last bar
    if (blockData.bars.length > 0) {
      const firstBar = blockData.bars[0];
      const lastBar = blockData.bars[blockData.bars.length - 1];
      this.spawnBlockLabel(blockNumber, firstBar, lastBar, blockData.color);
    }
  }

  /**
   * Spawn a block completion label centered between first and last bar
   * Uses horizontal layout if there's space, otherwise vertical
   */
  private spawnBlockLabel(blockNumber: number, firstBar: Bar, lastBar: Bar, color: number): void {
    const blockNumberStr = String(blockNumber);

    // Get verification status and add indicator (only in dev mode)
    let labelWithIcon = blockNumberStr;
    if (this.devMode) {
      const verification = this.getBlockVerification ? this.getBlockVerification(blockNumber) : null;
      const statusIcon = this.getVerificationIcon(verification?.status || 'pending');
      labelWithIcon = `${blockNumberStr} ${statusIcon}`;
    }

    const labelText = new Text({
      text: labelWithIcon,
      style: {
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 14,
        fontWeight: '700',
        fill: color,
      }
    });

    // Start invisible for fade-in animation
    labelText.alpha = 0;

    // Calculate block width and determine if vertical layout is needed
    const blockWidth = (lastBar.birthScrollOffset - firstBar.birthScrollOffset) + this.BAR_WIDTH;
    const textWidth = labelText.width;
    const useVertical = blockWidth < textWidth;

    // Calculate center scroll offset between first and last bar (needed before positioning)
    const centerScrollOffset = (firstBar.birthScrollOffset + lastBar.birthScrollOffset) / 2;

    // Position label offset by center scroll (container transform will add current scroll)
    // This centers the label between first and last bar of the block
    // Includes SPAWN_OFFSET to match bar positioning
    labelText.x = this.SPAWN_X + this.SPAWN_OFFSET - centerScrollOffset;
    labelText.anchor.set(0.5, 0); // Center horizontally, anchor at top

    if (useVertical) {
      // Vertical text for narrow blocks (rotated 90° counter-clockwise)
      // Adjust Y position to maintain same distance from bars as horizontal text
      labelText.y = 15 + labelText.height / 2;
      labelText.rotation = -Math.PI / 2;
    } else {
      // Horizontal text for wide blocks
      labelText.y = 15; // Below the chart (baseline is at Y=0)
      labelText.rotation = 0;
    }

    this.barsContainer.addChild(labelText); // Add to barsContainer for transform-based scrolling

    // Add to xLabels array so it scrolls with the chart
    const label: XLabel = {
      text: labelText,
      x: this.SPAWN_X,
      birthScrollOffset: centerScrollOffset,
      startTime: performance.now(),
      blockNumber: blockNumber
    };
    this.xLabels.push(label);
  }

  /**
   * Get verification icon based on status
   */
  private getVerificationIcon(status: 'pending' | 'verified' | 'warning'): string {
    switch (status) {
      case 'pending':
        return '⏳'; // Pending verification
      case 'verified':
        return '✅'; // All miniblocks received
      case 'warning':
        return '⚠️'; // Missing miniblocks
      default:
        return '⏳';
    }
  }

  /**
   * Update block label verification status
   * Called when verification status changes
   */
  public updateBlockVerification(blockNumber: number): void {
    // Only show icons in dev mode
    if (!this.devMode) return;

    // Find label for this block
    const label = this.xLabels.find(l => l.blockNumber === blockNumber);
    if (!label) return;

    // Get updated verification status
    const verification = this.getBlockVerification ? this.getBlockVerification(blockNumber) : null;
    const statusIcon = this.getVerificationIcon(verification?.status || 'pending');
    const blockNumberStr = String(blockNumber);

    // Update label text
    label.text.text = `${blockNumberStr} ${statusIcon}`;
  }

  /**
   * Set dev mode (shows verification icons when true)
   */
  public setDevMode(enabled: boolean): void {
    this.devMode = enabled;
  }

  /**
   * Set callback for block verification
   */
  public setBlockVerificationCallback(callback: (blockNumber: number) => { blockNumber: number; expected: number; received: number; status: 'pending' | 'verified' | 'warning' } | null): void {
    this.getBlockVerification = callback;
  }


  /**
   * Clear all bars
   */
  public clear(): void {
    for (const bar of this.bars) {
      this.barsContainer.removeChild(bar.graphics);
      this.returnGraphicsToPool(bar.graphics); // Return to pool instead of destroying
    }
    for (const label of this.xLabels) {
      this.barsContainer.removeChild(label.text);
    }
    for (const label of this.yAxisLabels) {
      this.container.removeChild(label);
    }
    for (const bg of this.yAxisLabelBackgrounds) {
      this.container.removeChild(bg);
      bg.destroy();
    }
    this.bars = [];
    this.xLabels = [];
    this.yAxisLabels = [];
    this.yAxisLabelBackgrounds = [];
    this.yAxisLabelBackgroundWidths = [];
    this.barGasValues.clear();
    this.barTxValues.clear();
    this.scrollOffset = 0;
    this.targetScrollOffset = 0;
    this.barCounter = 0;
    this.spawnTimer = 0;

    // Reset dynamic scaling
    this.currentGasMax = this.MINIBLOCK_GAS_MAX;
    this.currentTxMax = this.MINIBLOCK_TX_MAX;
    this.targetGasMax = this.MINIBLOCK_GAS_MAX;
    this.targetTxMax = this.MINIBLOCK_TX_MAX;
  }

  /**
   * Set callback for when a bar is clicked
   */
  public setBarClickCallback(callback: (miniblock: MiniblockData) => void): void {
    this.onBarClick = callback;
  }

  /**
   * Get the future X position from the movement queue
   * Uses linear interpolation between queue entries for perfect accuracy
   */
  public getSpawnIndicatorFutureX(travelTime: number): number {
    const now = performance.now();
    const targetTime = now + travelTime;

    // OPTIMIZATION: Use pre-allocated circular buffer
    // Find entries before and after target time for interpolation
    let entryBeforeX = 0;
    let entryBeforeTime = 0;
    let hasEntryBefore = false;
    let entryAfterX = 0;
    let entryAfterTime = 0;
    let hasEntryAfter = false;

    for (let i = 0; i < this.movementQueueLength; i++) {
      const idx = (this.movementQueueHead + i) % this.MOVEMENT_QUEUE_SIZE;
      const moveTime = this.movementQueueTime[idx];
      const moveX = this.movementQueueX[idx];

      if (moveTime < targetTime) {
        entryBeforeX = moveX;
        entryBeforeTime = moveTime;
        hasEntryBefore = true;
      } else {
        entryAfterX = moveX;
        entryAfterTime = moveTime;
        hasEntryAfter = true;
        break;
      }
    }

    // If we have both entries, interpolate between them
    if (hasEntryBefore && hasEntryAfter) {
      const timeDiff = entryAfterTime - entryBeforeTime;
      const progress = (targetTime - entryBeforeTime) / timeDiff;
      return entryBeforeX + (entryAfterX - entryBeforeX) * progress;
    }

    // If only after entry exists, use it
    if (hasEntryAfter) {
      return entryAfterX;
    }

    // If only before entry exists, use the last planned position
    if (this.movementQueueLength > 0) {
      const lastTailIndex = (this.movementQueueTail - 1 + this.MOVEMENT_QUEUE_SIZE) % this.MOVEMENT_QUEUE_SIZE;
      return this.movementQueueX[lastTailIndex];
    }

    // Fallback to current position
    return this.spawnIndicatorX;
  }

  /**
   * Get the Y position of the spawn indicator
   */
  public getSpawnIndicatorY(): number {
    return this.BASELINE_Y + this.SPAWN_INDICATOR_OFFSET_Y;
  }

  /**
   * Notify the sequencer that transactions have hit it
   * This accelerates the spinning animation (beam intensity is synced automatically)
   * @param count - Number of transactions that hit
   */
  public onTransactionHit(count: number = 1): void {
    this.sequencer.onTransactionHit(count);
  }

  /**
   * Update paused state (bars are only clickable when paused)
   * Enables/disables interaction to prevent FPS drops from event system
   */
  public setPausedState(isPaused: boolean): void {
    this.isPaused = isPaused;
    // Toggle interaction based on pause state for performance
    // When paused: enable clicks to inspect bars
    // When running: disable all events to maximize FPS
    // IMPORTANT: Only toggle container-level eventMode, NOT individual bars
    // Setting eventMode on individual bars causes expensive hit-testing on 500-1000+ objects
    this.container.eventMode = isPaused ? 'static' : 'none';
    this.container.cursor = isPaused ? 'default' : 'default';
  }

  /**
   * Apply theme colors to all visual elements
   */
  private applyTheme(): void {
    // Redraw baseline (same color as grid lines)
    this.baselineGraphic.clear();
    this.baselineGraphic.moveTo(0, 0);
    this.baselineGraphic.lineTo(SCENE_WIDTH, 0);
    this.baselineGraphic.stroke({ width: 1, color: this.colors.border, alpha: 0.5 });

    // Sequencer handles its own theme updates via ThemeManager subscription

    // Update Y-axis labels
    for (const label of this.yAxisLabels) {
      label.style.fill = this.colors.textSecondary;
    }

    // Redraw Y-axis label backgrounds
    for (let i = 0; i < this.yAxisLabelBackgrounds.length; i++) {
      const bgGraphic = this.yAxisLabelBackgrounds[i];
      const labelText = this.yAxisLabels[i];
      if (bgGraphic && labelText) {
        const padding = 4;
        const boxHeight = labelText.height + padding * 2;
        const halfBoxHeight = boxHeight / 2;
        const boxWidth = labelText.width + padding * 2;
        bgGraphic.clear();
        bgGraphic.rect(0, -halfBoxHeight, boxWidth, boxHeight);
        bgGraphic.fill(this.colors.bgSurface);
      }
    }

    // Redraw grid lines
    this.drawGridLines();
  }

  /**
   * Set callback for when sequencer is clicked
   */
  public setSequencerClickCallback(callback: (mouseX: number, mouseY: number) => void): void {
    this.sequencer.setClickCallback(callback);
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    if (this.unsubscribeTheme) {
      this.unsubscribeTheme();
    }
    this.clear();
    // Destroy sequencer
    this.sequencer.destroy();
    // Destroy all pooled Graphics objects
    for (const graphics of this.graphicsPool) {
      graphics.destroy();
    }
    this.graphicsPool = [];
    this.container.destroy({ children: true });
  }
}
